import { Box, } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Translations } from "ui/Scripts/source/models/translations";
import { Dialog } from "../../../../../framework/Dialog/Dialog";
import { DialogButtons } from "../../../../../framework/Dialog/DialogButtons";
import { IdTitle, IIdTitle } from "ui/Scripts/source/models/common/idTitle";
import { Base } from "ui/Scripts/source/framework/base";
import { DialogSettingsCard } from "source/components/framework/Container/DialogSettingsCard";
import { ActionContainer } from "source/components/framework/ActionContainer";
import { ListIconButtonsItem } from "source/components/framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { List } from "source/components/framework/List/List";
import { FormDesignerOptionDialog } from "./FormDesignerOptionDialog";
import { ListLinkValue } from "source/components/framework/List/ListLinkValue";
import { ArrowUpToolButton } from "source/components/framework/Button/ArrowUpToolButton";
import { ArrowDownToolButton } from "source/components/framework/Button/ArrowDownToolButton";
import { AppUtils } from "source/framework/appUtils";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";

interface IFormDesignerOptionsDialogProps {
    isRadio: boolean;
    idOptions: IIdTitle[];
    onSave: (idOptions: IIdTitle[]) => Promise<void>;
    onClose: () => void;
}

export const FormDesignerOptionsDialog = (
    props: IFormDesignerOptionsDialogProps
) => {
    const orgStateHash = React.useRef<string>("");
    const [idOptions, setIdOptions] = useState<IIdTitle[]>([]);
    const [selectionData, setSelectionData] = useState<{ title: string, index: number }>({ title: "", index: -1 });
    const [optionDialogData, setOptionDialogData] = useState<{ idOption: IIdTitle, index: number, isNew: boolean, usedOptionTitles: string[] }>({ idOption: null, index: 0, isNew: false, usedOptionTitles: [] });

    const getHashFromIdOptions = (value: IIdTitle[]): string => {
        return JSON.stringify(value);
    };

    useEffect(() => {
        const newIdOptions = props.idOptions.map(i => IdTitle.createIdTitle(i.id, i.title));
        setIdOptions(newIdOptions);
        orgStateHash.current = getHashFromIdOptions(newIdOptions);
    }, [props.idOptions]);

    const handleSetSelectedIds = (title: string, checkedIds: string[]) => {
        const index = idOptions.findIndex(i => i.id === title);
        setSelectionData({
            title,
            index
        });
    };
    
    const handleOpen = (title: string) => {
        const index = idOptions.findIndex(i => i.title === title);
        if (index < 0) return;
        setOptionDialogData({
            idOption: idOptions[index],
            index,
            isNew: false,
            usedOptionTitles: idOptions.filter((i, idx) => idx !== index).map(i => i.title)
        });
    };

    const handleOptionDialogClose = () => {
        setOptionDialogData({
            idOption: null,
            index: 0,
            isNew: false,
            usedOptionTitles: []
        });
    };

    const handleOptionDialogSaved = (idOption: IIdTitle) => {
        if (!idOption) return;
        if (optionDialogData.isNew) {
            setIdOptions((old) => [...old, idOption]);
            setOptionDialogData((old) => ({
                ...old,
                isNew: false,
                idOption,
                index: idOptions.length
            }));
        } else {
            setIdOptions((old) => {
                old[optionDialogData.index] = idOption;
                return [...old];
            });
        }
        handleOptionDialogClose(); //Close!!!
    };

    const handleRemove = (title: string) => {
        if (!title) return;
        handleOptionDialogClose();
        setIdOptions((old) => [...old.filter((item) => item.title !== title)]);
        setSelectionData({
            title: "",
            index: -1
        });
    };

    const handleAdd = () => {
        setOptionDialogData({
            idOption: IdTitle.createIdTitleSimple(Translations.FormOption + " " + (idOptions.length + 1).toString(10)),
            index: -1,
            isNew: true,
            usedOptionTitles: idOptions.map(i => i.title)
        });
    };

    const handleSave = () => {
        if (idOptions.length < 1) {
            AppUtils.showErrorMessage(Translations.AtLeastOneFormOptionMustBeDefined);
            return;
        }
        props.onSave(idOptions);
    };

    const setEditDialogItemByIndex = (index: number) => {
        setOptionDialogData({
            idOption: idOptions[index],
            index,
            isNew: false,
            usedOptionTitles: idOptions.map(i => i.title)
        });
        setSelectionData({
            title: idOptions[index].title,
            index
        });
    };

    const handleMoveToPrevious = () => {
        if (!optionDialogData.idOption || optionDialogData.index < 1 || optionDialogData.index > idOptions.length) return;
        setEditDialogItemByIndex(optionDialogData.index - 1);
    };

    const handleMoveToNext = () => {
        if (!optionDialogData.idOption || optionDialogData.index < 0 || optionDialogData.index >= idOptions.length - 1) return;
        setEditDialogItemByIndex(optionDialogData.index + 1);
    };
    
    const swapIdOptions = (oldIndex: number, newIndex: number) => {
        const newIdOptions = Base.swapArrayItems(idOptions.slice(0), oldIndex, newIndex);
        setIdOptions(newIdOptions);
        setSelectionData({
            title: newIdOptions[newIndex].title,
            index: newIndex
        });
    };

    const handleMoveItemUp = () => {
        if (selectionData.index < 1) return;
        swapIdOptions(selectionData.index, selectionData.index - 1);
    };
    
    const handleMoveItemDown = () => {
        if (selectionData.index < 0 || selectionData.index >= idOptions.length - 1) return;
        swapIdOptions(selectionData.index, selectionData.index + 1);
    };

    const handleClose = async () => {
        const cancelResult = await AppUtils.cancel(getHashFromIdOptions(idOptions), orgStateHash.current);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            handleSave();
        } else {
            props.onClose();
        }
    };

    return (
        <Dialog
            id="task-form-designer-component-properties-options-dialog"
            maxWidth="sm"
            maxHeight="600px"
            title={props.isRadio ? Translations.FormRadioOptionsDialog : Translations.FormDropdownOptionsDialog}
            body={
                <>
                    <DialogSettingsCard
                        fitToContainerHeight
                        showCard={false}
                        cardContent={
                            <ActionContainer
                                margin="0"
                                rightItems={
                                    <ListIconButtonsItem
                                        prefixElement={
                                            <div>
                                                <ArrowUpToolButton onClick={handleMoveItemUp} disabled={selectionData.index < 1} />
                                                <ArrowDownToolButton onClick={handleMoveItemDown} disabled={selectionData.index < 0 || selectionData.index >= idOptions.length - 1} />
                                            </div>
                                        }
                                        removeDisabled={!selectionData.title}
                                        onAdd={handleAdd}
                                        onRemove={() => handleRemove(selectionData.title)}
                                    />
                                }
                            />
                        }
                        content={
                            <List
                                intialized={true}
                                isLoading={false}
                                hasMore={false}
                                globalLoadingInUse={true}
                                height="100%"
                                header={{
                                    columns: [
                                        { size: 6, column: "title", content: Translations.Name },
                                        { size: 6, column: "id", content: Translations.Value }
                                    ]
                                }}
                                lines={idOptions.map((idOption) => {
                                    return {
                                        columns: [
                                            { size: 6, content: <ListLinkValue onClick={() => handleOpen(idOption.title)}>{idOption.title}</ListLinkValue> },
                                            { size: 6, content: idOption.id }
                                        ],
                                        id: idOption.title
                                    };
                                })}
                                sortColumn={""}
                                sortOrderIsAsc={true}
                                selectedId={selectionData.title}
                                checkedIds={[]}
                                onSetSelectedIds={handleSetSelectedIds}
                                onSearchLines={() => {}}
                                onOpenLine={handleOpen}
                            />
                        } />
                    {!!optionDialogData.idOption &&
                        <FormDesignerOptionDialog
                            usedOptionTitles={optionDialogData.usedOptionTitles}
                            idOption={optionDialogData.idOption}
                            isRadio={props.isRadio}
                            isNew={optionDialogData.isNew}
                            hasPrevious={optionDialogData.index > 0}
                            hasNext={optionDialogData.index < idOptions.length - 1}
                            onSaved={handleOptionDialogSaved}
                            onClose={handleOptionDialogClose}
                            onRemove={handleRemove}
                            onPrevious={handleMoveToPrevious}
                            onNext={handleMoveToNext}
                        />
                    }
                </>
            }
            actions={
                <Box display="flex" justifyContent="flex-end" width="100%">
                    <DialogButtons
                        saveTitle={Translations.OK}
                        onCancel={handleClose}
                        onSave={handleSave}
                    />
                </Box>
            }
            onClose={handleClose}
        />
    );
};
